import React from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import {
  Button,
  Card,
  ErrorBoundary,
  Form,
  Page,
  QueryResult,
  Toast,
} from "@sscs/ui";
import { SaveIcon } from "@sscs/ui/icons";
import { getQueryStatus } from "@sscs/utils";
import { PERMISSION } from "Constants";
import { useAllowedTo, useCurrentOrganization, useRedirect } from "Hooks";
import { apiErrorToast } from "Utils";
import {
  useGetAiConfigQuery,
  useUpdateAiConfigMutation,
} from "../../services/aiSettings.api";
import { AiConfigurationFormData, GetAiConfigResponse } from "../../types";
import { AiConfigurationFormSchema } from "../../utils";
import styles from "./aiSettings.module.scss";

const AI_CONFIGURATION_DEFAULTS = {
  ai_enabled: false,
  generate_package_overview_enabled: false,
  generate_software_info_enabled: false,
} satisfies GetAiConfigResponse;

const AiSettings = () => {
  const canConfigureAi = useAllowedTo({
    permissions: [PERMISSION.MODIFY_AI_CONFIGURATION],
  });
  useRedirect({
    to: "/page-not-found",
    when: !canConfigureAi,
  });

  const currentOrganization = useCurrentOrganization();

  const {
    data: aiConfiguration = AI_CONFIGURATION_DEFAULTS,
    isFetching,
    isLoading,
    isError,
    error,
  } = useGetAiConfigQuery(
    { organizationId: currentOrganization?.id || "" },
    { skip: !currentOrganization },
  );

  const [updateAiConfiguration] = useUpdateAiConfigMutation();

  const formMethods = useForm<AiConfigurationFormData>({
    resolver: yupResolver(AiConfigurationFormSchema),
    mode: "onChange",
    values: aiConfiguration,
  });

  const {
    formState: { isDirty, isSubmitting },
    handleSubmit,
    watch,
  } = formMethods;

  const onSubmit: SubmitHandler<AiConfigurationFormData> = async (data) => {
    try {
      if (currentOrganization) {
        await updateAiConfiguration({
          organizationId: currentOrganization.id,
          body: { ...data },
        }).unwrap();

        Toast.show({
          title: "AI configuration updated",
          type: "success",
        });
      }
    } catch (error) {
      apiErrorToast(error, "Could not update AI configuration");
    }
  };

  const isAiEnabled = watch("ai_enabled");

  const result = (
    <Page.Root>
      <Page.Header
        title="AI usage configuration"
        actions={
          <Button
            type="submit"
            form="ai-configuration-form"
            color="primary"
            disabled={!isDirty || isSubmitting}
          >
            <SaveIcon />
            Save
          </Button>
        }
      />
      <Page.Body>
        <QueryResult
          status={getQueryStatus({
            isFetching,
            isLoading,
            isError,
            error,
            data: aiConfiguration,
          })}
        >
          <FormProvider {...formMethods}>
            <Form.Form
              id="ai-configuration-form"
              onSubmit={handleSubmit(onSubmit)}
              name="ai-configuration-form"
              className={styles["ai-configuration-form"]}
            >
              <Card.Card>
                <Card.Title>AI usage</Card.Title>
                <Card.Content>
                  <div className={styles["section-container"]}>
                    <Form.Checkbox
                      id="ai-enabled"
                      name="ai_enabled"
                      label={
                        <span className={styles["checkbox-label"]}>
                          Enable AI
                        </span>
                      }
                      helperText="Master switch for all AI features. While it is off, no AI feature runs regardless of the toggles below."
                      className={styles["form-checkbox"]}
                    />
                  </div>
                </Card.Content>
              </Card.Card>
              <Card.Card>
                <Card.Title>AI features</Card.Title>
                <Card.Content>
                  <div className={styles["section-container"]}>
                    <Form.Checkbox
                      id="generate-package-overview-enabled"
                      name="generate_package_overview_enabled"
                      label={
                        <span className={styles["checkbox-label"]}>
                          Package overview generation
                        </span>
                      }
                      helperText="Allow generating package summaries and overviews with AI."
                      className={styles["form-checkbox"]}
                      isDisabled={!isAiEnabled}
                    />
                    <Form.Checkbox
                      id="generate-software-info-enabled"
                      name="generate_software_info_enabled"
                      label={
                        <span className={styles["checkbox-label"]}>
                          Software info generation
                        </span>
                      }
                      helperText="Allow auto-filling software information with AI."
                      className={styles["form-checkbox"]}
                      isDisabled={!isAiEnabled}
                    />
                  </div>
                </Card.Content>
              </Card.Card>
            </Form.Form>
          </FormProvider>
        </QueryResult>
      </Page.Body>
    </Page.Root>
  );

  return <ErrorBoundary>{result}</ErrorBoundary>;
};

export default AiSettings;
